import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bell } from "lucide-react";
import { toast } from "sonner";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function NotificationsBell({ variant = "header" }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null); // notification object
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markRead = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* noop */ }
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
  };

  const openNotifDetail = async (n) => {
    setOpen(false);
    await markRead(n);
    if (n.link) nav(n.link);
  };

  const openReply = async (e, n) => {
    e.stopPropagation();
    setOpen(false);
    await markRead(n);
    setReplyBody("");
    setReplyTo(n);
  };

  const sendReply = async () => {
    if (!replyTo?.request_id) return;
    const body = replyBody.trim();
    if (!body) {
      toast.error("Escribe una respuesta");
      return;
    }
    setSending(true);
    try {
      await api.post(`/reuse-requests/${replyTo.request_id}/messages`, { body });
      toast.success("Respuesta enviada.");
      setReplyTo(null);
      setReplyBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No fue posible enviar la respuesta");
    } finally {
      setSending(false);
    }
  };

  const btnClass = variant === "lobby"
    ? "relative p-2.5 rounded-full bg-white shadow-sm border border-[#E2E8F0] text-[#0A2540] hover:bg-[#F5F1EA] transition-colors"
    : "relative p-2 rounded-sm text-[#0A2540] hover:bg-[#EAF0F6] transition-colors";

  const renderNotifBody = (n) => {
    // If sender + message are present (reuse_request enriched format), show "[user] dice: [message]" with clickable sender.
    if (n.sender_name && n.message) {
      return (
        <div className="text-sm text-[#0A2540] leading-snug">
          <button
            type="button"
            onClick={(e) => openReply(e, n)}
            className="font-semibold text-[#0A2540] hover:text-[#10B981] underline underline-offset-2 decoration-[#10B981]/40 hover:decoration-[#10B981] transition-colors"
            data-testid={`notif-sender-${n.id}`}
            aria-label={`Responder a ${n.sender_name}`}
          >
            {n.sender_name}
          </button>
          <span className="text-[#0A2540]"> dice: </span>
          <span className="text-[#0A2540]">{n.message}</span>
        </div>
      );
    }
    return <div className="text-sm text-[#0A2540] leading-snug">{n.text}</div>;
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className={btnClass} aria-label="Notificaciones" data-testid="notifications-bell">
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E11D48] text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0">
          <div className="px-4 py-3 border-b border-[#E2E8F0] text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">Notificaciones</div>
          <div className="max-h-96 overflow-auto divide-y divide-[#F1F5F9]">
            {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#5C6A7D]">Sin notificaciones.</div>}
            {items.map((n) => (
              <div
                key={n.id}
                onClick={() => openNotifDetail(n)}
                className={`w-full text-left px-4 py-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors ${n.read ? "" : "bg-[#EFF6FF]"}`}
                data-testid={`notification-item-${n.id}`}
              >
                {renderNotifBody(n)}
                <div className="text-[10px] text-[#94A3B8] mt-1">{fmtTime(n.created_at)}</div>
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Direct reply dialog */}
      <Dialog open={!!replyTo} onOpenChange={(o) => !o && setReplyTo(null)}>
        <DialogContent className="rounded-sm" data-testid="notif-reply-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Responder a {replyTo?.sender_name || "usuario"}
            </DialogTitle>
          </DialogHeader>
          {replyTo?.message && (
            <div className="text-xs text-[#5C6A7D] bg-[#FAFAFA] border border-[#E2E8F0] rounded-sm p-3">
              <span className="font-semibold text-[#0A2540]">{replyTo.sender_name}</span> dice: {replyTo.message}
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Tu respuesta</Label>
            <Textarea
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              placeholder="Escribe tu respuesta…"
              className="rounded-sm min-h-[120px]"
              data-testid="notif-reply-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplyTo(null)} className="rounded-sm">Cancelar</Button>
            <Button
              onClick={sendReply}
              disabled={sending}
              className="bg-[#10B981] hover:bg-[#059669] text-white rounded-sm"
              data-testid="notif-reply-send"
            >
              {sending ? "Enviando…" : "Enviar respuesta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
